import { useState, useEffect, type ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LEGAL_VERSION } from '../../shared/legalVersion';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import { PulseRing } from './ui/ScoreRing';
import { listOAuthProviders, confirmEmailVerification } from '../services/pocketbase';

type AuthView = 'login' | 'register' | 'reset' | 'check-email' | 'mfa-challenge';

export default function AuthGate({ children }: { children: ReactNode }) {
  const { user, isLoading, mfaRequired, recoveryMode, oauthError, signIn, signUp, signInWithGoogle, resetPassword, updatePassword, verifyMfaChallenge, resendMfaCode, signOut } = useAuth();
  const [view, setView] = useState<AuthView>('login');

  // Le bouton Google n'est rendu que si le fournisseur est réellement activé
  // côté PocketBase. Sinon il échoue au clic, sans que rien n'explique pourquoi
  // — et un bouton qui casse devant un client coûte plus cher qu'un bouton
  // absent. Le jour où le fournisseur est configuré dans l'admin PocketBase, il
  // réapparaît de lui-même, sans livraison.
  //
  // ⚠️ Avant d'activer OAuth2 : l'inscription Google ne passe PAS par Express,
  // donc SIGNUP_ALLOWLIST ne la filtre pas (cf. services/signupAllowlist.ts).
  // ── Vérification d'email ──────────────────────────────────────────────
  // Le lien du mail de confirmation atterrit sur /verify-email?token=… .
  // PocketBase enverrait par défaut vers sa propre interface d'administration,
  // que le proxy refuse en production (cf. pocketbase/mail-templates/README.md).
  //
  // L'état pilote l'affichage, PAS l'URL : le jeton est retiré de la barre
  // d'adresse dès qu'il est consommé, pour qu'il ne traîne ni dans l'historique
  // ni dans un partage de lien.
  const [verifyState, setVerifyState] = useState<'idle' | 'pending' | 'ok' | 'error'>('idle');
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.pathname.startsWith('/verify-email')) return;
    const token = url.searchParams.get('token');
    if (!token) {
      setVerifyState('error');
      window.history.replaceState({}, '', '/');
      return;
    }
    setVerifyState('pending');
    void confirmEmailVerification(token)
      .then(() => setVerifyState('ok'))
      .catch(() => setVerifyState('error'))
      .finally(() => window.history.replaceState({}, '', '/'));
  }, []);

  const [googleEnabled, setGoogleEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void listOAuthProviders().then((providers) => {
      if (!cancelled) setGoogleEnabled(providers.includes('google'));
    });
    return () => { cancelled = true; };
  }, []);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [recoveryDone, setRecoveryDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaResent, setMfaResent] = useState(false);
  // Acceptation des CGU + Politique de confidentialité (RGPD art. 13 + preuve de consentement éclairé).
  // Obligatoire pour créer un compte. La date et la version sont propagées à
  // profiles.terms_accepted_at / terms_version via le trigger handle_new_user().
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  // Loading state
  if (isLoading) {
    return (
      <div className="app-shell relative flex min-h-screen items-center justify-center overflow-hidden">
        <PulseRing size={72} />
      </div>
    );
  }

  // ── Écran de vérification d'email ────────────────────────────────────
  // Placé AVANT le court-circuit `user` plus bas : quelqu'un déjà connecté qui
  // clique le lien doit voir le résultat, pas être renvoyé dans l'application
  // sans explication.
  if (verifyState === 'pending') {
    return (
      <div className="app-shell relative flex min-h-screen items-center justify-center overflow-hidden">
        <PulseRing size={72} />
      </div>
    );
  }

  if (verifyState === 'ok' || verifyState === 'error') {
    const ok = verifyState === 'ok';
    return (
      <AuthLayout>
        <div className="text-center">
          {ok ? (
            <svg className="w-12 h-12 mx-auto text-khaki-600 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <path d="m9 11 3 3L22 4" />
            </svg>
          ) : (
            <svg className="w-12 h-12 mx-auto text-slate-400 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v5" />
              <path d="M12 16h.01" />
            </svg>
          )}
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {ok ? 'Adresse confirmée' : 'Lien inutilisable'}
          </h2>
          <p className="text-sm text-slate-500 mb-6">
            {ok
              ? 'Votre compte est activé. Connectez-vous pour accéder à vos analyses.'
              : "Ce lien a déjà été utilisé, ou il a expiré. Essayez de vous connecter : si votre adresse est déjà confirmée, tout fonctionnera normalement."}
          </p>
          <button
            onClick={() => { setVerifyState('idle'); setView('login'); }}
            className="text-sm text-violet-600 hover:underline"
          >
            Aller à la connexion
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Password recovery — the user followed a reset link. A session now exists,
  // so this MUST be checked before the `user` short-circuit below, otherwise
  // they'd drop straight into the app with no way to set a new password.
  if (recoveryMode && !recoveryDone) {
    const handleNewPassword = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      if (newPassword.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('Les deux mots de passe ne correspondent pas.');
        return;
      }
      setLoading(true);
      const result = await updatePassword(newPassword);
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setNewPassword('');
        setConfirmPassword('');
        setRecoveryDone(true);
      }
    };

    return (
      <AuthLayout>
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Nouveau mot de passe</h2>
        <p className="text-sm text-slate-500 mb-6">
          Choisissez un nouveau mot de passe pour votre compte.
        </p>
        {error && <ErrorBanner message={error} />}
        <form onSubmit={handleNewPassword} className="space-y-4">
          <Input label="Nouveau mot de passe" type="password" value={newPassword} onChange={setNewPassword} autoFocus />
          <Input label="Confirmer le mot de passe" type="password" value={confirmPassword} onChange={setConfirmPassword} />
          <SubmitButton loading={loading}>Enregistrer le mot de passe</SubmitButton>
        </form>
      </AuthLayout>
    );
  }

  // Password just reset — confirmation screen before entering the app.
  if (recoveryDone && !recoveryMode) {
    return (
      <AuthLayout>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-emerald-600 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <path d="m9 11 3 3L22 4" />
          </svg>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Mot de passe mis à jour</h2>
          {/* PocketBase invalide tous les jetons existants lors d'un reset :
              l'utilisateur doit se reconnecter, il ne retombe pas dans l'app. */}
          <p className="text-sm text-slate-500 mb-6">
            Votre mot de passe a été modifié avec succès. Connectez-vous avec votre nouveau mot de passe.
          </p>
          <button
            onClick={() => { setRecoveryDone(false); setView('login'); setPassword(''); }}
            className="text-sm text-violet-700 hover:underline"
          >
            Se connecter
          </button>
        </div>
      </AuthLayout>
    );
  }

  // Le mot de passe est validé mais PocketBase exige un second facteur : un code
  // à usage unique envoyé par email. Contrairement à Supabase, AUCUNE session
  // n'existe à ce stade (pas de niveau `aal1` intermédiaire) — un rafraîchissement
  // de page ramène donc à l'écran de connexion, ce qui est le comportement sûr.
  if (mfaRequired) {
    const handleMfaSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setMfaResent(false);
      // La longueur du code est configurable côté PocketBase (8 caractères par
      // défaut) : on ne la fige pas ici, on vérifie juste qu'il est saisi.
      if (mfaCode.length < 4) {
        setError('Saisissez le code reçu par email.');
        return;
      }
      setLoading(true);
      const result = await verifyMfaChallenge(mfaCode);
      setLoading(false);
      if (result.error) {
        setError(result.error);
        setMfaCode('');
      }
    };

    const handleMfaResend = async () => {
      setError(null);
      setMfaResent(false);
      setLoading(true);
      const result = await resendMfaCode();
      setLoading(false);
      if (result.error) setError(result.error);
      else setMfaResent(true);
    };

    return (
      <AuthLayout>
        <div className="text-center mb-4">
          <div className="inline-flex w-12 h-12 rounded-full bg-rose-pastel-200 items-center justify-center mb-3">
            <svg className="w-6 h-6 text-violet-700" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Authentification à deux facteurs</h2>
          <p className="text-sm text-slate-500">
            Un code à usage unique vient d'être envoyé à <strong className="text-slate-600">{email}</strong>.
            Saisissez-le pour terminer la connexion.
          </p>
        </div>

        {error && <ErrorBanner message={error} />}
        {mfaResent && !error && (
          <div className="mb-4 px-3 py-2 rounded-lg bg-emerald-100 border border-emerald-200 text-sm text-emerald-700">
            Un nouveau code vient d'être envoyé.
          </div>
        )}

        <form onSubmit={handleMfaSubmit} className="space-y-4">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={16}
            value={mfaCode}
            onChange={e => setMfaCode(e.target.value.replace(/\s+/g, ''))}
            autoFocus
            placeholder="Code reçu par email"
            disabled={loading}
            className="w-full rounded-full border border-slate-300 bg-cream-app px-4 py-3 text-center font-mono text-xl tracking-[0.3em] text-slate-900 outline-none placeholder:font-sans placeholder:text-sm placeholder:tracking-normal placeholder:text-slate-400"
          />
          <SubmitButton loading={loading}>Vérifier</SubmitButton>
        </form>

        <p className="mt-4 text-center text-xs text-slate-500">
          Code non reçu ?{' '}
          <button
            onClick={handleMfaResend}
            disabled={loading}
            className="text-violet-700 hover:underline disabled:opacity-50"
          >
            En renvoyer un
          </button>
        </p>

        <p className="mt-2 text-center text-xs text-slate-500">
          Plus d'accès à cette boîte email ?{' '}
          <button
            onClick={() => { setMfaCode(''); setError(null); setMfaResent(false); signOut(); }}
            className="text-violet-700 hover:underline"
          >
            Annuler la connexion
          </button>
        </p>
      </AuthLayout>
    );
  }

  // Authenticated — render app
  if (user) {
    return <>{children}</>;
  }

  // ── Check email view ──
  if (view === 'check-email') {
    return (
      <AuthLayout>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-violet-500 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Vérifiez votre email</h2>
          <p className="text-sm text-slate-500 mb-6">
            Un lien de confirmation a été envoyé à <strong className="text-slate-900">{email}</strong>. Cliquez dessus pour activer votre compte.
          </p>
          <button
            onClick={() => setView('login')}
            className="text-sm text-violet-600 hover:underline"
          >
            Retour à la connexion
          </button>
        </div>
      </AuthLayout>
    );
  }

  // ── Reset password view ──
  if (view === 'reset') {
    const handleReset = async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setLoading(true);
      const result = await resetPassword(email);
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setView('check-email');
      }
    };

    return (
      <AuthLayout>
        <h2 className="text-lg font-semibold text-slate-900 mb-1">Réinitialiser le mot de passe</h2>
        <p className="text-sm text-slate-500 mb-6">
          Entrez votre email pour recevoir un lien de réinitialisation.
        </p>
        {error && <ErrorBanner message={error} />}
        <form onSubmit={handleReset} className="space-y-4">
          <Input label="Email" type="email" value={email} onChange={setEmail} autoFocus />
          <SubmitButton loading={loading}>Envoyer le lien</SubmitButton>
        </form>
        <p className="mt-4 text-center text-sm text-slate-500">
          <button onClick={() => { setView('login'); setError(null); }} className="text-violet-600 hover:underline">
            Retour à la connexion
          </button>
        </p>
      </AuthLayout>
    );
  }

  // ── Login / Register views ──
  const isLogin = view === 'login';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (isLogin) {
      const result = await signIn(email, password);
      setLoading(false);
      if (result.error) setError(result.error);
      // `result.requiresMfa` n'a rien à faire ici : `signIn` a déjà armé
      // `mfaRequired` dans le contexte, ce qui bascule sur la vue de défi.
    } else {
      if (password.length < 6) {
        setError('Le mot de passe doit contenir au moins 6 caractères.');
        setLoading(false);
        return;
      }
      if (!termsAccepted) {
        setError("Vous devez accepter les CGU et la politique de confidentialité pour créer un compte.");
        setLoading(false);
        return;
      }
      const result = await signUp(email, password, {
        version: LEGAL_VERSION,
        acceptedAt: new Date().toISOString(),
      });
      setLoading(false);
      if (result.error) {
        setError(result.error);
      } else {
        setView('check-email');
      }
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-lg font-semibold text-slate-900 mb-1">
        {isLogin ? 'Connexion' : 'Créer un compte'}
      </h2>
      <p className="text-sm text-slate-500 mb-6">
        {isLogin
          ? 'Connectez-vous pour accéder à vos analyses.'
          : 'Créez votre compte pour commencer à analyser vos contenus.'}
      </p>

      {googleEnabled && (
        <>
        {/* Google OAuth — flux popup du SDK PocketBase : la page n'est pas
            rechargée, l'authStore est renseigné au retour de la popup. */}
        <button
          onClick={() => { setError(null); void signInWithGoogle(); }}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-slate-300 bg-cream-app px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          Continuer avec Google
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-slate-200" />
          <span className="text-xs text-slate-400">ou</span>
          <div className="flex-1 h-px bg-slate-200" />
        </div>
        </>
      )}

      {/* `oauthError` porte l'échec du flux Google (popup bloquée/fermée), que
          `signInWithGoogle` ne peut pas renvoyer avec sa signature `void`. */}
      {(error ?? oauthError) && <ErrorBanner message={error ?? oauthError!} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input label="Email" type="email" value={email} onChange={setEmail} autoFocus />
        <Input label="Mot de passe" type="password" value={password} onChange={setPassword} />

        {/* Case à cocher CGU + Politique de confidentialité — uniquement à l'inscription */}
        {!isLogin && (
          <label className="flex items-start gap-2.5 text-xs text-slate-600 leading-relaxed cursor-pointer select-none">
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={e => setTermsAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 bg-cream-app text-violet-500 focus:ring-violet-500 focus:ring-offset-0 cursor-pointer flex-shrink-0"
            />
            <span>
              J'ai lu et j'accepte les{' '}
              <button
                type="button"
                onClick={() => setShowTerms(true)}
                className="underline text-slate-900 hover:text-violet-700 transition-colors"
              >
                Conditions Générales d'Utilisation
              </button>
              {' '}et la{' '}
              <button
                type="button"
                onClick={() => setShowPrivacy(true)}
                className="underline text-slate-900 hover:text-violet-700 transition-colors"
              >
                Politique de confidentialité
              </button>
              {' '}de Visaly.
            </span>
          </label>
        )}

        <SubmitButton loading={loading}>
          {isLogin ? 'Se connecter' : "S'inscrire"}
        </SubmitButton>
      </form>

      {/* Modales légales accessibles avant l'inscription */}
      <PrivacyPolicy
        visible={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
      <TermsOfService
        visible={showTerms}
        onClose={() => setShowTerms(false)}
      />

      {isLogin && (
        <p className="mt-3 text-center text-xs text-slate-900">
          <button onClick={() => { setView('reset'); setError(null); }} className="text-slate-900 hover:underline">
            Mot de passe oublié ?
          </button>
        </p>
      )}

      <p className="mt-6 text-center text-sm text-slate-900">
        {isLogin ? "Pas encore de compte ?" : 'Déjà un compte ?'}{' '}
        <button
          onClick={() => {
            setView(isLogin ? 'register' : 'login');
            setError(null);
            setTermsAccepted(false);
          }}
          className="text-slate-900 hover:underline font-medium"
        >
          {isLogin ? "S'inscrire" : 'Se connecter'}
        </button>
      </p>
    </AuthLayout>
  );
}

// ── Sub-components ──────────────────────────────────────

/* ── Vitrine du split-screen ─────────────────────────────────────────
   Quatre volets qui tournent toutes les 5 s. Motif « carrousel » du WAI :
   — la rotation s'arrête DÉFINITIVEMENT dès que l'utilisateur prend la
     main (clic sur une pastille), le survol et le focus la mettent
     seulement en pause ;
   — sous `prefers-reduced-motion`, elle ne démarre jamais.
   Sur mobile la vitrine ne disparaît pas : elle se réduit à une bande.
   Une page de connexion nue ne dit plus quel produit on ouvre. */
const SHOWCASE = [
  {
    tint: 'bg-sun-400',
    tab: 'Pré-score',
    title: 'Un score sur 100, avant production',
    lead: "Brief, claim ou maquette PLV : le verdict tombe pendant que l'axe est encore modifiable.",
  },
  {
    tint: 'bg-rose-pastel-300',
    tab: '3 juges',
    title: 'Défense, contradicteur, arbitre',
    lead: "Deux modèles plaident en sens inverse, un troisième tranche. Le grand score est un arbitrage, pas une moyenne.",
  },
  {
    tint: 'bg-sky-pastel-300',
    tab: 'Sources',
    title: 'Chaque réserve renvoie à son article',
    lead: "Articles L5122 et R5122 du Code de la santé publique, consultés sur Légifrance, et doctrine publiée de l'ANSM.",
  },
  {
    tint: 'bg-khaki-300',
    tab: 'Souveraineté',
    title: 'Vos briefs restent en Europe',
    lead: "Traitement par Mistral, hébergement France et Union européenne. Aucun entraînement de modèle sur vos données.",
  },
];

function AuthShowcase() {
  const [index, setIndex] = useState(0);
  const [taken, setTaken] = useState(false); // l'utilisateur a pris la main
  const [paused, setPaused] = useState(false); // survol / focus

  useEffect(() => {
    if (taken || paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % SHOWCASE.length), 5000);
    return () => clearInterval(id);
  }, [taken, paused]);

  const item = SHOWCASE[index];

  return (
    <section
      aria-label="Aperçu de Visaly"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      className={`relative flex flex-col justify-between overflow-hidden border-b-2 border-ink p-6 transition-colors duration-700 lg:border-b-0 lg:border-r-2 lg:p-12 ${item.tint}`}
    >
      {/* Marque — sortie de secours pour qui arrive ici par erreur */}
      <a href="/" className="hidden items-center gap-2.5 lg:flex" aria-label="Visaly — accueil">
        <img src="/visaly_logo_blue.svg" alt="" aria-hidden className="h-8 w-8 select-none" draggable={false} />
        <span className="font-display text-2xl leading-none text-ink">Visaly</span>
      </a>

      <div className="lg:max-w-md">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-ink/50">
          {item.tab}
        </p>
        <h2 className="font-display text-2xl leading-tight text-ink lg:text-[2.4rem]">
          {item.title}
        </h2>
        <p className="mt-3 hidden text-base leading-relaxed text-ink/70 lg:block">
          {item.lead}
        </p>
      </div>

      {/* Pastilles de navigation — prendre la main arrête la rotation */}
      <div role="tablist" aria-label="Volets" className="mt-6 flex gap-2">
        {SHOWCASE.map((s, i) => (
          <button
            key={s.tab}
            role="tab"
            aria-selected={i === index}
            aria-label={s.tab}
            onClick={() => { setIndex(i); setTaken(true); }}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-8 bg-ink' : 'w-4 bg-ink/25 hover:bg-ink/45'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function AuthLayout({ children }: { children: ReactNode }) {
  return (
    <div className="app-shell min-h-screen p-0 sm:p-3 md:p-4">
      <div className="app-frame grid min-h-screen overflow-hidden rounded-none sm:min-h-[calc(100vh-1.5rem)] sm:rounded-[26px] md:min-h-[calc(100vh-2rem)] md:rounded-[32px] lg:grid-cols-[1.05fr_.95fr]">
        <AuthShowcase />

        {/* Colonne formulaire */}
        <section className="flex flex-col justify-between gap-8 p-6 sm:p-10 lg:p-12">
          {/* Marque, version mobile (la vitrine ne l'affiche qu'en desktop) */}
          <a href="/" className="flex items-center gap-2.5 lg:hidden" aria-label="Visaly — accueil">
            <img src="/visaly_logo_blue.svg" alt="" aria-hidden className="h-7 w-7 select-none" draggable={false} />
            <span className="font-display text-xl leading-none text-slate-900">Visaly</span>
          </a>

          {/* `flex-1` centre le formulaire dans la hauteur restante : sans lui,
              `justify-between` le collerait en haut de colonne sur grand écran. */}
          <div className="flex flex-1 items-center">
            <div className="mx-auto w-full max-w-sm">{children}</div>
          </div>

          <div className="mx-auto w-full max-w-sm">
            <p className="text-[11px] leading-relaxed text-slate-400">
              Visaly est un outil d'aide à la décision. Il ne remplace ni la revue réglementaire du laboratoire, ni le visa de l'ANSM, ni un avis juridique.
            </p>
            <a href="/landing.html" className="mt-3 inline-block text-xs text-slate-500 underline underline-offset-2 transition-colors hover:text-slate-800">
              ← Retour à l'accueil
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}

function Input({ label, type, value, onChange, autoFocus }: {
  label: string;
  type: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="glow-input rounded-full">
        <input
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          autoFocus={autoFocus}
          required
          className="w-full rounded-full border border-slate-300 bg-cream-app px-4 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400"
        />
      </div>
    </div>
  );
}

function SubmitButton({ loading, children }: { loading: boolean; children: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="w-full rounded-full bg-ink py-3 text-sm font-medium text-white transition-transform hover:scale-[1.02] active:scale-100 disabled:opacity-50 disabled:hover:scale-100"
    >
      {loading ? 'Chargement…' : children}
    </button>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="mb-4 rounded-2xl bg-red-100 px-4 py-2.5 text-sm text-red-700">
      {message}
    </div>
  );
}
